/**
 * state.ts — the GUI v2 live-panel state machine.
 *
 * One reducer consumes CoachingEvents (v1 + v2 — a v1-only engine still renders
 * a working panel) plus local panel actions. Panel actions are applied
 * OPTIMISTICALLY here and also sent upstream as PanelMessages (the engine's
 * snapshot echoes reconcile; manual wins on both sides).
 *
 * Lifecycle (GUI v2 feature 8): idle → incoming → live → wrapup.
 */
import { BODY_TO_CLASS } from "@/live/content/pricing";
import {
  RUBRIC,
  STAGE_ZONE,
  type BehaviorKey,
  type BodyStyle,
  type CallCardState,
  type ChecklistState,
  type CoachingEvent,
  type ComplianceAlertId,
  type CriterionKey,
  type Nudge,
  type NudgeMeta,
  type ReasonKey,
  type ServiceInterest,
  type StageId,
  type TranscriptLine,
  type ZoneKey,
} from "@/types";

/** Cap accumulated transcript lines so a long call can't grow state unbounded. */
const TRANSCRIPT_MAX = 300;

export type LivePhase = "idle" | "incoming" | "live" | "wrapup";

export type PanelNudge = Nudge & NudgeMeta;

export interface ComplianceHit {
  alertId: ComplianceAlertId;
  atMs: number;
}

export interface LiveV2State {
  phase: LivePhase;
  counterpart: string | null;
  startedAt: string | null;
  endedAt: string | null;
  card: CallCardState;
  stageId: StageId;
  checklist: ChecklistState;
  /** Active nudges, newest first; the panel renders at most 2 (req A.3). */
  nudges: PanelNudge[];
  buyingSignal: boolean;
  behaviors: BehaviorKey[];
  /** Detected objections (ids), newest last. */
  detectedObjections: string[];
  /** The objection card currently open (detected or manually tapped). */
  activeObjectionId: string | null;
  /** Latest un-dismissed compliance alert. */
  compliance: ComplianceHit | null;
  complianceHistory: ComplianceHit[];
  lastCustomerLine: string | null;
  /** Accumulated final transcript lines (rep + customer), for the transcript panel. */
  transcript: TranscriptLine[];
  priceStated: boolean;
  pinnedValuePoints: string[];
  disposition: string | null;
}

function emptyField<T>() {
  return { value: null as T | null, confirmed: false, source: null };
}

export function freshCard(): CallCardState {
  return {
    firstName: emptyField<string>(),
    lastName: emptyField<string>(),
    phone: emptyField<string>(),
    serviceInterest: emptyField<ServiceInterest>(),
    reasons: [],
    vehicles: [
      {
        year: emptyField<number>(),
        make: emptyField<string>(),
        model: emptyField<string>(),
        bodyStyle: emptyField<BodyStyle>(),
        pricingClass: null,
        classAssumed: false,
        ambiguous: false,
        existingTint: null,
      },
    ],
    activeVehicle: 0,
  };
}

export function freshChecklist(): ChecklistState {
  return RUBRIC.map((c) => ({
    ...c,
    covered: false,
    coveredAtMs: null,
    evidence: null,
    manual: false,
    subItems:
      c.key === "captured_customer_vehicle_reasons"
        ? [
            { key: "name", label: "Name", covered: false, evidence: null },
            { key: "vehicle", label: "Vehicle", covered: false, evidence: null },
            { key: "reason", label: "Reason", covered: false, evidence: null },
          ]
        : null,
  }));
}

export function initialState(): LiveV2State {
  return {
    phase: "idle",
    counterpart: null,
    startedAt: null,
    endedAt: null,
    card: freshCard(),
    stageId: 1,
    checklist: freshChecklist(),
    nudges: [],
    buyingSignal: false,
    behaviors: [],
    detectedObjections: [],
    activeObjectionId: null,
    compliance: null,
    complianceHistory: [],
    lastCustomerLine: null,
    transcript: [],
    priceStated: false,
    pinnedValuePoints: [],
    disposition: null,
  };
}

export type LiveAction =
  | { kind: "event"; event: CoachingEvent }
  | { kind: "expireNudge"; id: string }
  | { kind: "dismissNudge"; id: string }
  | { kind: "doneNudge"; id: string }
  | { kind: "openObjection"; id: string | null }
  | { kind: "dismissCompliance" }
  | { kind: "toggleChecklist"; key: CriterionKey }
  | { kind: "confirmReason"; key: ReasonKey; state: "confirmed" | "removed" }
  | { kind: "setCardField"; field: "firstName" | "lastName"; value: string }
  | { kind: "setVehicle"; index: number; patch: { year?: number | null; make?: string | null; model?: string | null; bodyStyle?: BodyStyle | null } }
  | { kind: "setServiceInterest"; value: ServiceInterest }
  | { kind: "setActiveVehicle"; index: number }
  | { kind: "togglePin"; id: string }
  | { kind: "setDisposition"; key: string }
  | { kind: "reset" };

export function currentZone(state: LiveV2State): ZoneKey {
  return STAGE_ZONE[state.stageId];
}

function applyEvent(state: LiveV2State, event: CoachingEvent): LiveV2State {
  switch (event.type) {
    case "hello":
      return state;
    case "call_ringing":
      return { ...initialState(), phase: "incoming", counterpart: event.counterpart };
    case "call_started":
      return { ...initialState(), phase: "live", counterpart: event.counterpart, startedAt: event.startedAt };
    case "call_ended":
      return { ...state, phase: "wrapup", endedAt: event.endedAt, nudges: [], compliance: null };
    case "call_card":
      return { ...state, phase: state.phase === "idle" ? "live" : state.phase, card: event.card };
    case "stage":
      return { ...state, phase: state.phase === "idle" ? "live" : state.phase, stageId: event.stageId };
    case "checklist":
      return { ...state, phase: state.phase === "idle" ? "live" : state.phase, checklist: event.checklist };
    case "rubric": {
      // v1 fallback: adopt coverage into the richer checklist shape.
      const checklist = state.checklist.map((item) => {
        const v1 = event.rubric.find((c) => c.key === item.key);
        if (!v1 || item.manual) return item;
        return { ...item, covered: v1.covered, coveredAtMs: v1.coveredAtMs };
      });
      return { ...state, phase: state.phase === "idle" ? "live" : state.phase, checklist };
    }
    case "behavior": {
      const behaviors = state.behaviors.includes(event.key) ? state.behaviors : [...state.behaviors, event.key];
      return { ...state, behaviors, priceStated: state.priceStated || event.key === "price_stated" };
    }
    case "nudge": {
      const rest = state.nudges.filter((n) => n.id !== event.nudge.id);
      return {
        ...state,
        phase: state.phase === "idle" ? "live" : state.phase,
        nudges: [event.nudge, ...rest].slice(0, 4),
      };
    }
    case "objection": {
      const detected = state.detectedObjections.includes(event.objectionId)
        ? state.detectedObjections
        : [...state.detectedObjections, event.objectionId];
      return { ...state, detectedObjections: detected, activeObjectionId: event.objectionId };
    }
    case "buying_signal":
      return { ...state, buyingSignal: true };
    case "compliance_alert": {
      const hit = { alertId: event.alertId, atMs: event.evidence?.atMs ?? 0 };
      return { ...state, compliance: hit, complianceHistory: [...state.complianceHistory, hit] };
    }
    case "transcript": {
      if (!event.line.final) return state; // interim lines churn — commit finals only
      const transcript = [...state.transcript, event.line].slice(-TRANSCRIPT_MAX);
      return {
        ...state,
        transcript,
        lastCustomerLine: event.line.speaker === "customer" ? event.line.text : state.lastCustomerLine,
      };
    }
    default:
      return state;
  }
}

export function reducer(state: LiveV2State, action: LiveAction): LiveV2State {
  switch (action.kind) {
    case "reset":
      return initialState();
    case "event":
      return applyEvent(state, action.event);
    case "expireNudge":
    case "dismissNudge":
    case "doneNudge": {
      const nudge = state.nudges.find((n) => n.id === action.id);
      const next = { ...state, nudges: state.nudges.filter((n) => n.id !== action.id) };
      if (action.kind === "doneNudge" && nudge?.criterionKey) {
        next.checklist = state.checklist.map((c) =>
          c.key === nudge.criterionKey ? { ...c, covered: true, manual: true } : c,
        );
      }
      return next;
    }
    case "openObjection":
      return { ...state, activeObjectionId: action.id };
    case "dismissCompliance":
      return { ...state, compliance: null };
    case "toggleChecklist":
      return {
        ...state,
        checklist: state.checklist.map((c) =>
          c.key === action.key ? { ...c, covered: !c.covered, manual: true } : c,
        ),
      };
    case "confirmReason": {
      const reasons =
        action.state === "removed"
          ? state.card.reasons.filter((r) => r.key !== action.key)
          : state.card.reasons.some((r) => r.key === action.key)
            ? state.card.reasons.map((r) => (r.key === action.key ? { ...r, confirmed: true } : r))
            : [...state.card.reasons, { key: action.key, confirmed: true }];
      return { ...state, card: { ...state.card, reasons } };
    }
    case "setCardField":
      return {
        ...state,
        card: {
          ...state.card,
          [action.field]: { value: action.value || null, confirmed: action.value !== "", source: "manual" },
        },
      };
    case "setVehicle": {
      const vehicles = state.card.vehicles.map((v, i) => {
        if (i !== action.index) return v;
        const next = { ...v };
        if (action.patch.year !== undefined)
          next.year = { value: action.patch.year, confirmed: action.patch.year !== null, source: "manual" };
        if (action.patch.make !== undefined)
          next.make = { value: action.patch.make, confirmed: action.patch.make !== null, source: "manual" };
        if (action.patch.model !== undefined)
          next.model = { value: action.patch.model, confirmed: action.patch.model !== null, source: "manual" };
        if (action.patch.bodyStyle !== undefined)
          next.bodyStyle = {
            value: action.patch.bodyStyle,
            confirmed: action.patch.bodyStyle !== null,
            source: "manual",
          };
        // Deterministic class resolution (mirrors the engine; req 1.6/5.2).
        const body = next.bodyStyle.value;
        if (body) {
          next.pricingClass = BODY_TO_CLASS[body];
          next.classAssumed = false;
          next.ambiguous = false;
        } else {
          next.ambiguous = next.pricingClass === null && (next.model.value !== null || next.make.value !== null);
        }
        return next;
      });
      return { ...state, card: { ...state.card, vehicles } };
    }
    case "setServiceInterest":
      return {
        ...state,
        card: { ...state.card, serviceInterest: { value: action.value, confirmed: true, source: "manual" } },
      };
    case "setActiveVehicle":
      return { ...state, card: { ...state.card, activeVehicle: action.index } };
    case "togglePin":
      return {
        ...state,
        pinnedValuePoints: state.pinnedValuePoints.includes(action.id)
          ? state.pinnedValuePoints.filter((p) => p !== action.id)
          : [...state.pinnedValuePoints, action.id],
      };
    case "setDisposition":
      return { ...state, disposition: action.key };
    default:
      return state;
  }
}
